import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../views/style/siteHeader.css";
import { useAuth } from "../context/AuthContext";
import { requestPasswordReset } from "../services/authService";

const PENDING_ANALYSIS_KEY = "finpilot_pending_analysis_query";

const buildSearchParams = (query) => {
  const trimmed = String(query || "").trim();
  const params = new URLSearchParams();
  params.set("query", trimmed);

  if (/^\d{8}$/.test(trimmed)) {
    params.set("analyze", "1");
  } else {
    params.set("search", "1");
  }

  return params.toString();
};

function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [authError, setAuthError] = useState("");
  const [resetStatus, setResetStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    password: "",
    inviteCode: "",
  });
  const { user, login, register, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasInviteCode, setHasInviteCode] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authMode = params.get("auth");
    if (authMode !== "login" && authMode !== "register") return;

    setAuthTab(authMode);
    setAuthOpen(true);
    setAuthError("");
    setResetStatus("");
  }, [location.search]);

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const openAuth = (tab) => {
    setAuthTab(tab);
    setAuthOpen(true);
    setAuthError("");
    setResetStatus("");
  };

  const closeAuth = () => {
    setAuthOpen(false);
  };

  const redirectAfterAuth = () => {
    const pendingQuery = window.localStorage.getItem(PENDING_ANALYSIS_KEY);
    if (pendingQuery) {
      window.localStorage.removeItem(PENDING_ANALYSIS_KEY);
      navigate(`/app/vyhledani-firmy?${buildSearchParams(pendingQuery)}`);
      return;
    }

    navigate("/app/home");
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setAuthError("");
    setResetStatus("");
    try {
      await login({ email: loginForm.email, password: loginForm.password });
      setAuthOpen(false);
      setLoginForm({ email: "", password: "" });
      redirectAfterAuth();
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setAuthError("");
    setResetStatus("");
    try {
      await register({
        name: registerForm.name,
        email: registerForm.email,
        password: registerForm.password,
        inviteCode: registerForm.inviteCode,
      });
      setAuthOpen(false);
      setRegisterForm({ name: "", email: "", password: "", inviteCode: "" });
      redirectAfterAuth();
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordReset = async () => {
    setResetStatus("");
    if (!loginForm.email) {
      setResetStatus("Zadejte prosím e-mail pro reset hesla.");
      return;
    }
    try {
      await requestPasswordReset(loginForm.email);
      setResetStatus("Pokud účet existuje, poslali jsme e-mail s resetem hesla.");
    } catch (err) {
      setResetStatus(err.message);
    }
  };

  return (
    <header className="site-header">
      <div className="site-header__inner">
        <Link to="/" className="site-header__brand">
          <img className="site-header__logo" src="/minilogo.svg" alt="" width="32" height="32" />
          <span className="site-header__wordmark"><span>Fin</span><span>Pilot</span></span>
        </Link>

        <button className="site-header__burger" type="button" onClick={toggleMenu} aria-label="Otevřít menu">
          <span />
          <span />
          <span />
        </button>

        <nav className={`site-header__nav ${menuOpen ? "is-open" : ""}`}>
          {user ? (
            <Link to="/app/vyhledani-firmy" onClick={() => setMenuOpen(false)}>
              Vyhledání firmy
            </Link>
          ) : null}
          <Link to="/oborove-statistiky" onClick={() => setMenuOpen(false)}>
            Oborové statistiky
          </Link>
          <Link to="/benchmark-obory" onClick={() => setMenuOpen(false)}>
            Benchmark oborů
          </Link>
          <Link to="/obory" onClick={() => setMenuOpen(false)}>
            Přehled oborů
          </Link>
          <Link to="/blog" onClick={() => setMenuOpen(false)}>
            Blog
          </Link>
          {user ? (
            <>
              <Link to="/app/profile" className="site-header__mobile-only" onClick={() => setMenuOpen(false)}>
                Nastavení profilu
              </Link>
              <button
                className="site-header__mobile-action site-header__mobile-only"
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
              >
                Odhlásit se
              </button>
            </>
          ) : null}
        </nav>

        <div className="site-header__actions">
          {user ? (
            <>
              <Link className="site-button" to="/app/home">
                Domů
              </Link>
              <Link
                className="site-button site-button--icon site-header__desktop-user-action"
                to="/app/profile"
                aria-label="Nastavení profilu"
                title="Nastavení"
              >
                ⚙️
              </Link>
              <button
                className="site-button site-button--primary site-header__desktop-user-action"
                type="button"
                onClick={logout}
              >
                Odhlásit se
              </button>
            </>
          ) : (
            <>
              <button className="site-button" type="button" onClick={() => openAuth("login")}>
                Přihlášení
              </button>
              <button className="site-button site-button--primary" type="button" onClick={() => openAuth("register")}>
                Registrace
              </button>
            </>
          )}
        </div>
      </div>

      {authOpen && (
        <div className="auth-modal" role="dialog" aria-modal="true">
          <div className="auth-modal__card">
            <button className="auth-modal__close" type="button" onClick={closeAuth}>
              ✕
            </button>

            <div className="auth-modal__tabs">
              <button
                type="button"
                className={authTab === "login" ? "is-active" : ""}
                onClick={() => {
                  setAuthTab("login");
                  setAuthError("");
                }}
              >
                Přihlášení
              </button>
              <button
                type="button"
                className={authTab === "register" ? "is-active" : ""}
                onClick={() => {
                  setAuthTab("register");
                  setAuthError("");
                }}
              >
                Registrace
              </button>
            </div>

            {authTab === "login" ? (
              <form className="auth-modal__form" onSubmit={handleLoginSubmit}>
                <label>
                  E-mail
                  <input
                    type="email"
                    placeholder="např. jana@firma.cz"
                    value={loginForm.email}
                    onChange={(event) => setLoginForm((prev) => ({ ...prev, email: event.target.value }))}
                  />
                </label>
                <label>
                  Heslo
                  <input
                    type="password"
                    placeholder="Zadejte heslo"
                    value={loginForm.password}
                    onChange={(event) => setLoginForm((prev) => ({ ...prev, password: event.target.value }))}
                  />
                </label>
                {authError && <span className="auth-modal__error">{authError}</span>}
                {resetStatus && <span className="auth-modal__hint">{resetStatus}</span>}
                <button className="site-button site-button--primary" type="submit" disabled={submitting}>
                  {submitting ? "Přihlašuji..." : "Přihlásit se"}
                </button>
                <button className="site-button" type="button" onClick={handlePasswordReset}>
                  Poslat reset hesla
                </button>
              </form>
            ) : (
              <form className="auth-modal__form" onSubmit={handleRegisterSubmit}>
                <label>
                  Jméno a příjmení
                  <input
                    type="text"
                    placeholder="např. Jana Nováková"
                    value={registerForm.name}
                    onChange={(event) => setRegisterForm((prev) => ({ ...prev, name: event.target.value }))}
                  />
                </label>
                <label>
                  E-mail
                  <input
                    type="email"
                    placeholder="např. jana@firma.cz"
                    value={registerForm.email}
                    onChange={(event) => setRegisterForm((prev) => ({ ...prev, email: event.target.value }))}
                  />
                </label>
                <label>
                  Heslo
                  <input
                    type="password"
                    placeholder="Min. 8 znaků"
                    value={registerForm.password}
                    onChange={(event) => setRegisterForm((prev) => ({ ...prev, password: event.target.value }))}
                  />
                </label>

                <label className="auth-modal__checkbox-label">
                  <input
                    type="checkbox"
                    checked={hasInviteCode}
                    onChange={(event) => setHasInviteCode(event.target.checked)}
                  />
                  <span>Mám pozvánkový kód</span>
                </label>

                {hasInviteCode && (
                  <label className="auth-modal__field-animate">
                    <input
                      type="text"
                      placeholder="Zadejte kod"
                      value={registerForm.inviteCode}
                      onChange={(event) =>
                        setRegisterForm((prev) => ({ ...prev, inviteCode: event.target.value }))
                      }
                    />
                  </label>
                )}

                {authError && <span className="auth-modal__error">{authError}</span>}
                <button className="site-button site-button--primary" type="submit" disabled={submitting}>
                  {submitting ? "Registruji..." : "Vytvořit účet"}
                </button>
                <span className="auth-modal__hint">Registrací souhlasíte s podmínkami služby.</span>
              </form>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default SiteHeader;
